import { useCallback, useEffect, useRef } from 'react'

export const SEARCH_DEBOUNCE_MS = 500

// Share the typing timer with explicit submission so Enter cannot leave a
// second search scheduled. Identical in-flight searches share one promise.
export default function useDebouncedSearch(query, enabled, search) {
  const searchRef = useRef(search)
  searchRef.current = search
  const timerRef = useRef(null)
  const pendingRef = useRef(new Map())

  const searchNow = useCallback(() => {
    clearTimeout(timerRef.current)
    if (!enabled) return Promise.resolve()
    const pending = pendingRef.current
    if (pending.has(query)) return pending.get(query)

    const request = Promise.resolve()
      .then(() => searchRef.current(query))
      .finally(() => pending.delete(query))
    pending.set(query, request)
    return request
  }, [query, enabled])

  useEffect(() => {
    if (!enabled) return
    timerRef.current = setTimeout(() => {
      // Callers handle search errors for both manual and automatic searches.
      void searchNow().catch(() => {})
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timerRef.current)
  }, [enabled, searchNow])

  return searchNow
}
